import {
    AUTH_TOKEN_MAX_LENGTH, AuthType, DEFAULT_PORTS, FROM_NAME_MAX_LENGTH, HOST_MAX_LENGTH, PASSWORD_MAX_LENGTH,
    SaveSettingsRequest, SecurityType, SmtpSettings, USERNAME_MAX_LENGTH,
} from './models';

/** Editable form values. `port` is a string so the input can be blank. Secrets are only what the admin typed. */
export interface SmtpFormValue {
    host: string;
    port: string;
    securityType: SecurityType;
    authType: AuthType;
    username: string;
    password: string;
    authToken: string;
    fromEmail: string;
    fromName: string;
    replyTo: string;
}

export const MAILTRAP_PRESET: Partial<SmtpFormValue> = {
    host: 'live.smtp.mailtrap.io',
    port: '587',
    securityType: 'starttls',
    authType: 'token',
    username: 'api',
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
// Hostname, IPv4, or IPv6 (optionally bracketed). The server does the strict check.
const HOST_PATTERN = /^(\[?[0-9A-Fa-f:.]+\]?|[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?(\.[A-Za-z0-9]([A-Za-z0-9-]*[A-Za-z0-9])?)*)$/;

export function blankForm(): SmtpFormValue {
    return {
        host: '', port: String(DEFAULT_PORTS.starttls), securityType: 'starttls', authType: 'password',
        username: '', password: '', authToken: '', fromEmail: '', fromName: '', replyTo: '',
    };
}

/** Form values for the stored row. Platform defaults (`isDefault`) are never prefilled, and neither are secrets. */
export function formFromSettings(settings: SmtpSettings | null): SmtpFormValue {
    if (!settings || settings.isDefault) {
        return blankForm();
    }
    return {
        host: settings.host || '',
        port: settings.port ? String(settings.port) : '',
        securityType: settings.securityType || 'starttls',
        authType: settings.authType || 'password',
        username: settings.username || '',
        password: '',
        authToken: '',
        fromEmail: settings.fromEmail || '',
        fromName: settings.fromName || '',
        replyTo: settings.replyTo || '',
    };
}

/** The stored row, or null when the admin has saved nothing. */
function savedRow(settings: SmtpSettings | null): SmtpSettings | null {
    return settings && !settings.isDefault ? settings : null;
}

/** Whether the username and credential inputs apply. `none` cannot have a login. */
export function loginAllowed(form: SmtpFormValue): boolean {
    return form.securityType !== 'none';
}

/** Whether a saved secret of the current type can be kept by leaving the field blank. */
export function hasSavedSecret(form: SmtpFormValue, settings: SmtpSettings | null): boolean {
    const row = savedRow(settings);
    if (!row || row.authType !== form.authType) {
        // Switching authType makes the server drop the old secret.
        return false;
    }
    return form.authType === 'token' ? row.hasAuthToken : row.hasPassword;
}

export function passwordRequired(form: SmtpFormValue, settings: SmtpSettings | null): boolean {
    return loginAllowed(form) && form.authType === 'password' && !!form.username.trim() && !hasSavedSecret(form, settings);
}

export function authTokenRequired(form: SmtpFormValue, settings: SmtpSettings | null): boolean {
    return loginAllowed(form) && form.authType === 'token' && !hasSavedSecret(form, settings);
}

/** Port to show after the security type changes: follow the default unless the admin typed their own. */
export function portAfterSecurityChange(currentPort: string, previous: SecurityType, next: SecurityType): string {
    const port = currentPort.trim();
    if (!port || port === String(DEFAULT_PORTS[previous])) {
        return String(DEFAULT_PORTS[next]);
    }
    return currentPort;
}

/** Field errors keyed by field name. Empty when the form can be sent. */
export function validateForm(form: SmtpFormValue, settings: SmtpSettings | null): Partial<Record<keyof SmtpFormValue, string>> {
    const errors: Partial<Record<keyof SmtpFormValue, string>> = {};
    const host = form.host.trim();
    const port = form.port.trim();
    const username = form.username.trim();
    const fromEmail = form.fromEmail.trim();
    const replyTo = form.replyTo.trim();

    if (!host) {
        errors.host = 'Host is required.';
    } else if (host.length > HOST_MAX_LENGTH) {
        errors.host = `Host can be at most ${HOST_MAX_LENGTH} characters.`;
    } else if (!HOST_PATTERN.test(host)) {
        errors.host = 'Enter a hostname or IP address, without http:// or a path.';
    }

    if (port && !(/^\d+$/.test(port) && +port >= 1 && +port <= 65535)) {
        errors.port = 'Port must be a whole number from 1 to 65535.';
    }

    if (loginAllowed(form)) {
        if (form.authType === 'token' && !username) {
            errors.username = 'Username is required for token sign-in.';
        } else if (username.length > USERNAME_MAX_LENGTH) {
            errors.username = `Username can be at most ${USERNAME_MAX_LENGTH} characters.`;
        }

        if (passwordRequired(form, settings) && !form.password) {
            errors.password = 'Enter the password for this username.';
        } else if (form.password.length > PASSWORD_MAX_LENGTH) {
            errors.password = `Password can be at most ${PASSWORD_MAX_LENGTH} characters.`;
        }

        if (authTokenRequired(form, settings) && !form.authToken) {
            errors.authToken = 'Enter the API token.';
        } else if (form.authToken.length > AUTH_TOKEN_MAX_LENGTH) {
            errors.authToken = `Token can be at most ${AUTH_TOKEN_MAX_LENGTH} characters.`;
        }
    }

    if (!fromEmail) {
        errors.fromEmail = 'From email is required.';
    } else if (!EMAIL_PATTERN.test(fromEmail)) {
        errors.fromEmail = 'Enter a valid email address.';
    }

    if (form.fromName.trim().length > FROM_NAME_MAX_LENGTH) {
        errors.fromName = `From name can be at most ${FROM_NAME_MAX_LENGTH} characters.`;
    }

    if (replyTo && !EMAIL_PATTERN.test(replyTo)) {
        errors.replyTo = 'Enter a valid email address.';
    }

    return errors;
}

/**
 * Builds the saveSettings body. Secrets are sent only when typed and only for the
 * current auth type; `provider` is never sent. Cleared optional fields are sent as
 * null so the server removes them, and left out when nothing was stored.
 */
export function buildSaveRequest(form: SmtpFormValue, settings: SmtpSettings | null): SaveSettingsRequest {
    const row = savedRow(settings);
    const port = form.port.trim();
    const login = loginAllowed(form);
    const username = login ? form.username.trim() : '';
    const authType: AuthType = login ? form.authType : 'password';

    const body: SaveSettingsRequest = {
        host: form.host.trim(),
        port: port ? +port : DEFAULT_PORTS[form.securityType],
        securityType: form.securityType,
        authType,
        username: username || null,
        fromEmail: form.fromEmail.trim(),
    };

    if (login && authType === 'password' && username && form.password) {
        body.password = form.password;
    }
    if (login && authType === 'token' && form.authToken) {
        body.authToken = form.authToken;
    }

    const fromName = form.fromName.trim();
    if (fromName || row?.fromName) {
        body.fromName = fromName || null;
    }
    const replyTo = form.replyTo.trim();
    if (replyTo || row?.replyTo) {
        body.replyTo = replyTo || null;
    }

    return body;
}

/** True when the form differs from what is stored, including any typed secret. */
export function isFormDirty(form: SmtpFormValue, settings: SmtpSettings | null): boolean {
    if (form.password || form.authToken) {
        return true;
    }
    const saved = formFromSettings(settings);
    const keys: (keyof SmtpFormValue)[] = ['host', 'port', 'securityType', 'authType', 'username', 'fromEmail', 'fromName', 'replyTo'];
    return keys.some(key => form[key].trim() !== saved[key].trim());
}
